import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogContent,
  FormControlLabel, Switch, useMediaQuery, useTheme, IconButton
} from '@mui/material';
import { Close, Settings, Security, Analytics, Campaign, Extension } from '@mui/icons-material';

const ConsentBanner = ({mode}) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true, analytics: false, marketing: false, functional: false
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const saved = localStorage.getItem('user-consent');
    if (!saved) {
      setTimeout(() => setShowBanner(true), 1500);
    } else {
      setConsent(JSON.parse(saved));
    }
  }, []);

  const saveConsent = (data) => {
    localStorage.setItem('user-consent', JSON.stringify(data));
    setConsent(data);
    setShowBanner(false);
    setShowSettings(false);
    
    // Update Google Consent
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'ad_storage': data.marketing ? 'granted' : 'denied',
        'analytics_storage': data.analytics ? 'granted' : 'denied',
        'functionality_storage': data.functional ? 'granted' : 'denied'
      });
    }
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true, functional: true });
  const declineAll = () => saveConsent({ necessary: true, analytics: false, marketing: false, functional: false });

  const categories = [
    { key: 'necessary', title: 'Necessary', icon: Security, disabled: true },
    { key: 'analytics', title: 'Analytics', icon: Analytics },
    { key: 'marketing', title: 'Marketing', icon: Campaign },
    { key: 'functional', title: 'Functional', icon: Extension }
  ];

  if (!showBanner) return null;

  return (
    <>
      {/* Banner */}
      <Box sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: 3, p: 2
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row', gap: 2, alignItems: 'center' }}>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              We use cookies
            </Typography>
            <Typography variant="body2" color="darkTexts">
              We use cookies to improve your experience and analyze site usage.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button onClick={acceptAll} variant="contained" color={mode === 'dark' ? 'theGreen' : 'theOrange'}  >
              Accept All
            </Button>
            <Button onClick={declineAll} variant="outlined" color={mode === 'dark' ? 'theGreen' : 'theOrange'} >
              Decline
            </Button>
            <Button onClick={() => setShowSettings(true)} variant="outlined" 
              color={mode === 'dark' ? 'theGreen' : 'theOrange'} startIcon={<Settings />}>
              Settings
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="sm" fullWidth  slotProps={{ paper: {
        sx: { backgroundColor: theme.palette.background.default, borderRadius: 2 }
      }}}>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Cookie Settings</Typography>
            <IconButton onClick={() => setShowSettings(false)} size="small">
              <Close />
            </IconButton>
          </Box>

          {categories.map(({ key, title, icon: Icon, disabled }) => (
            <Box key={key} sx={{ 
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              py: 2, borderBottom: `1px solid ${theme.palette.divider}`,
              '&:last-child': { borderBottom: 'none' }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Icon color="action" />
                <Typography>{title}</Typography>
              </Box>
              <Switch
                checked={consent[key]}
                onChange={(e) => setConsent(prev => ({ ...prev, [key]: e.target.checked }))}
                disabled={disabled}
              />
            </Box>
          ))}

          <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end' }}>
            <Button onClick={declineAll} variant="outlined" color={mode === 'dark' ? 'theGreen' : 'theOrange'}>
              Decline All
            </Button>
            <Button onClick={() => saveConsent(consent)} variant="contained" color={mode === 'dark' ? 'theGreen' : 'theOrange'} >
              Save
            </Button>
            <Button onClick={acceptAll} variant="contained" color={mode === 'dark' ? 'theGreen' : 'theOrange'} >
              Accept All
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConsentBanner;